import { ResourceName } from "@/types";
import { useState, useCallback, useEffect } from "react";
import { useDataProvider, useGetIdentity } from "react-admin";

/**
 * Custom hook for managing user data lookup with caching and loading state management.
 * Prevents duplicate API calls and provides both async and sync access to cached user data.
 * Automatically includes current user in cache to avoid unnecessary API calls.
 */
export const useUserLookup = () => {
  const { data: currentUser } = useGetIdentity();

  // In-memory cache to store fetched user data (userId -> user object)
  // Initialize with current user if available to avoid fetching it again
  const [userCache, setUserCache] = useState<Record<string, any>>(() => {
    const initialCache: Record<string, any> = {};
    if (currentUser?.id) {
      initialCache[currentUser.id] = currentUser;
    }
    return initialCache;
  });

  // Track loading state for all users
  const [usersLoading, setUsersLoading] = useState<boolean>(false);

  const dataProvider = useDataProvider();

  // Update cache when current user changes
  useEffect(() => {
    if (currentUser?.id && !userCache[currentUser.id]) {
      setUserCache((prev) => ({ ...prev, [currentUser.id]: currentUser }));
    }
  }, [currentUser, userCache]);

  /**
   * Asynchronously fetch user data with intelligent caching and duplicate request prevention.
   * @param userId - The user ID to fetch
   * @returns Promise<user | null> - The user object or null if not found/failed
   */
  const getUser = useCallback(
    async (userId: string) => {
      // Validate user ID before making any requests
      if (!userId || userId === "0000000000000000" || userId.trim() === "" || userId.match(/^0+$/)) {
        return null;
      }

      // Return immediately if user is already cached (includes current user)
      if (userCache[userId]) {
        return userCache[userId];
      }

      // Mark this user ID as currently loading (both ref and state)
      setUsersLoading(true);

      try {
        // Fetch user data from the API
        const { data: user } = await dataProvider.getOne(ResourceName.USERS, {
          id: userId,
        });

        // Store successful result in cache for future use
        setUserCache((prev) => ({ ...prev, [userId]: user }));

        return user;
      } catch (error) {
        console.warn(`Failed to fetch user ${userId}:`, error);

        // Cache null result to prevent repeated failed requests for the same invalid user ID
        setUserCache((prev) => ({ ...prev, [userId]: null }));

        return null;
      } finally {
        setUsersLoading(false);
      }
    },
    [dataProvider, userCache]
  );

  /**
   * Synchronously get user data from cache only (no API call).
   * Useful for immediate access to previously fetched users.
   * @param userId - The user ID to lookup
   * @returns user | null - Cached user object or null if not in cache
   */
  const getUserSync = useCallback(
    (userId: string) => {
      // Validate user ID
      if (!userId || userId === "0000000000000000" || userId.trim() === "" || userId.match(/^0+$/)) {
        return null;
      }

      return userCache[userId] || null;
    },
    [userCache]
  );

  return {
    getUser, // Async fetch with caching and validation
    getUserSync, // Sync cache lookup with validation
    userCache, // Direct access to cache (useful for debugging or bulk operations)
    usersLoading,
  };
};
